import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import {
  legalService,
  LegalHold,
  HoldCustodian,
  HoldAuditEntry,
} from '../services/legal.service';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
};

const ACK_STATUS_COLORS: Record<string, string> = {
  PENDING: 'warning',
  ACKNOWLEDGED: 'success',
  NON_RESPONSIVE: 'danger',
};

const ACK_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  ACKNOWLEDGED: 'Acknowledged',
  NON_RESPONSIVE: 'Non-Responsive',
};

const inputCls =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
const labelCls = 'text-sm font-medium text-secondary-700 dark:text-secondary-300';

// ── Add Custodian Form ─────────────────────────────────────────────────────────

function AddCustodianForm({
  holdId,
  onAdded,
  onCancel,
}: {
  holdId: string;
  onAdded: (custodian: HoldCustodian) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    custodianName: '',
    custodianEmail: '',
    department: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.custodianName.trim() || !form.custodianEmail.trim()) {
      toast.error('Name and email are required');
      return;
    }
    try {
      setSubmitting(true);
      const created = await legalService.addCustodian(holdId, {
        custodianName: form.custodianName.trim(),
        custodianEmail: form.custodianEmail.trim(),
        department: form.department.trim() || undefined,
      });
      onAdded(created);
      toast.success('Custodian added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add custodian');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Full Name</label>
              <input
                type="text"
                value={form.custodianName}
                onChange={(e) => setForm((p) => ({ ...p, custodianName: e.target.value }))}
                className={cn(inputCls, 'mt-1')}
                required
                placeholder="Custodian name"
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={form.custodianEmail}
                onChange={(e) => setForm((p) => ({ ...p, custodianEmail: e.target.value }))}
                className={cn(inputCls, 'mt-1')}
                required
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className={labelCls}>Department</label>
              <input
                type="text"
                value={form.department}
                onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))}
                className={cn(inputCls, 'mt-1')}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Custodian'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function LegalHoldDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [hold, setHold] = useState<LegalHold | null>(null);
  const [custodians, setCustodians] = useState<HoldCustodian[]>([]);
  const [auditLog, setAuditLog] = useState<HoldAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddCustodian, setShowAddCustodian] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchHold = useCallback(async () => {
    if (!id) {return;}
    try {
      setLoading(true);
      const [holdData, logData] = await Promise.all([
        legalService.getHold(id),
        legalService.getHoldAuditLog(id).catch(() => []),
      ]);
      setHold(holdData);
      setCustodians(holdData.custodians ?? []);
      setAuditLog(logData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hold');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchHold();
  }, [fetchHold]);

  const handleActivate = async () => {
    if (!id) {return;}
    try {
      setActionLoading(true);
      const updated = await legalService.activateHold(id);
      setHold(updated);
      toast.success('Hold activated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to activate hold');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!id) {return;}
    try {
      setActionLoading(true);
      const updated = await legalService.releaseHold(id);
      setHold(updated);
      toast.success('Hold released');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to release hold');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcknowledge = async (custodianId: string) => {
    if (!id) {return;}
    try {
      const updated = await legalService.acknowledgeCustodian(id, custodianId);
      setCustodians((prev) => prev.map((c) => (c.id === custodianId ? updated : c)));
      toast.success('Acknowledged');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to acknowledge');
    }
  };

  const handleRemoveCustodian = async (custodianId: string) => {
    if (!id) {return;}
    try {
      await legalService.removeCustodian(id, custodianId);
      setCustodians((prev) => prev.filter((c) => c.id !== custodianId));
      toast.success('Custodian removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove custodian');
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center text-secondary-600 dark:text-secondary-400 py-12">
          Loading hold...
        </div>
      </div>
    );
  }

  if (error || !hold) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Hold
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Hold not found'}
            </p>
            <Button onClick={() => navigate('/legal/holds')} variant="outline">
              Back to Holds
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCfg = legalService.getHoldStatusConfig(hold.status);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate('/legal/holds')}
              className="mt-1 p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
                {hold.holdName}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-full',
                    STATUS_BADGE_COLORS[statusCfg.color]
                  )}
                >
                  {statusCfg.label}
                </span>
                {hold.matterId && (
                  <Link
                    to={`/legal/matters/${hold.matterId}`}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    View Matter
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-shrink-0">
            {hold.status === 'DRAFT' && (
              <Button onClick={handleActivate} disabled={actionLoading}>
                {actionLoading ? 'Activating...' : 'Activate Hold'}
              </Button>
            )}
            {hold.status === 'ACTIVE' && (
              <Button variant="outline" onClick={handleRelease} disabled={actionLoading}>
                {actionLoading ? 'Releasing...' : 'Release Hold'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Hold Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Scope */}
          {hold.scopeDescription && (
            <Card>
              <CardHeader>
                <CardTitle>Scope Description</CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <p className="text-sm text-secondary-700 dark:text-secondary-300 whitespace-pre-wrap">
                  {hold.scopeDescription}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Data Sources */}
          {hold.dataSources?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Data Sources</CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="flex flex-wrap gap-2">
                  {hold.dataSources.map((ds) => (
                    <span
                      key={ds}
                      className="px-3 py-1 bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 rounded-full text-sm"
                    >
                      {ds}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custodians */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Custodians</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddCustodian((v) => !v)}
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-6 space-y-4">
              {showAddCustodian && (
                <AddCustodianForm
                  holdId={hold.id}
                  onAdded={(c) => {
                    setCustodians((prev) => [...prev, c]);
                    setShowAddCustodian(false);
                  }}
                  onCancel={() => setShowAddCustodian(false)}
                />
              )}

              {custodians.length === 0 ? (
                <div className="text-center py-4 text-secondary-600 dark:text-secondary-400">
                  <UserGroupIcon className="w-8 h-8 mx-auto mb-2 text-secondary-400" />
                  No custodians added yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-secondary-200 dark:border-secondary-700">
                        <th className="text-left py-2 text-secondary-600 dark:text-secondary-400 font-medium pr-4">Name</th>
                        <th className="text-left py-2 text-secondary-600 dark:text-secondary-400 font-medium pr-4">Email</th>
                        <th className="text-left py-2 text-secondary-600 dark:text-secondary-400 font-medium pr-4">Dept.</th>
                        <th className="text-left py-2 text-secondary-600 dark:text-secondary-400 font-medium pr-4">Status</th>
                        <th className="text-left py-2 text-secondary-600 dark:text-secondary-400 font-medium pr-4">Ack. Date</th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {custodians.map((c) => {
                        const ackColor = ACK_STATUS_COLORS[c.ackStatus] ?? 'secondary';
                        return (
                          <tr
                            key={c.id}
                            className="border-b border-secondary-100 dark:border-secondary-800"
                          >
                            <td className="py-3 pr-4 font-medium text-secondary-900 dark:text-white">
                              {c.custodianName}
                            </td>
                            <td className="py-3 pr-4 text-secondary-600 dark:text-secondary-400">
                              {c.custodianEmail}
                            </td>
                            <td className="py-3 pr-4 text-secondary-600 dark:text-secondary-400">
                              {c.department ?? '—'}
                            </td>
                            <td className="py-3 pr-4">
                              <span
                                className={cn(
                                  'px-2 py-0.5 text-xs rounded-full',
                                  STATUS_BADGE_COLORS[ackColor]
                                )}
                              >
                                {ACK_STATUS_LABELS[c.ackStatus] ?? c.ackStatus}
                              </span>
                            </td>
                            <td className="py-3 pr-4 text-secondary-600 dark:text-secondary-400">
                              {legalService.formatDate(c.ackDate)}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-1">
                                {c.ackStatus === 'PENDING' && (
                                  <button
                                    onClick={() => handleAcknowledge(c.id)}
                                    title="Mark as acknowledged"
                                    className="p-1 rounded hover:bg-success-100 dark:hover:bg-success-900/20 text-secondary-400 hover:text-success-600 transition-colors"
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemoveCustodian(c.id)}
                                  title="Remove custodian"
                                  className="p-1 rounded hover:bg-danger-100 dark:hover:bg-danger-900/20 text-secondary-400 hover:text-danger-600 transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6 space-y-3">
              <div>
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  Issued Date
                </span>
                <p className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">
                  {legalService.formatDate(hold.issuedDate)}
                </p>
              </div>
              {hold.releasedDate && (
                <div>
                  <span className="text-xs text-secondary-500 dark:text-secondary-400">
                    Released Date
                  </span>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">
                    {legalService.formatDate(hold.releasedDate)}
                  </p>
                </div>
              )}
              <div>
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  Custodians
                </span>
                <p className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">
                  {custodians.length}
                </p>
              </div>
              <div>
                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                  Acknowledged
                </span>
                <p className="text-sm font-medium text-secondary-900 dark:text-white mt-0.5">
                  {custodians.filter((c) => c.ackStatus === 'ACKNOWLEDGED').length} /{' '}
                  {custodians.length}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {auditLog.length === 0 ? (
                <p className="text-sm text-secondary-500 dark:text-secondary-400">
                  No audit entries yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {auditLog.slice(0, 10).map((entry) => (
                    <div key={entry.id} className="flex items-start gap-2">
                      <ClockIcon className="w-4 h-4 text-secondary-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-secondary-900 dark:text-white">{entry.action}</p>
                        {entry.details && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">
                            {entry.details}
                          </p>
                        )}
                        <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">
                          {legalService.formatDate(entry.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
